import moment from "moment";
import {
  getTokenClinicDoctor,
  getToken as getPatToken,
} from "../../patient/utils/commonUtils";
import { showToast } from "../../patient/utils/toastUtils";
import { getToken } from "./tokenUtils";
import React from "react";
import { MdDateRange } from "react-icons/md";
import download from "downloadjs";
import { isNumber } from "lodash";
import dayjs from "dayjs";

export const processApiResponse = async (response, retrunMsg = false) => {
  try {
    if (response.ok) {
      const res = await response.json();
      if (retrunMsg) {
        return res;
      } else {
        return res.data;
      }
    } else {
      if (retrunMsg) {
        const errorMsg = await response.json();
        return errorMsg;
      } else {
        const errorMsg = await response.json();
        handleResponseError(
          response.status,
          errorMsg?.message || "Error on return",
          retrunMsg
        );
      }
    }
  } catch (error) {
    console.error("Error:", error);
    if (retrunMsg) {
      return handleResponseError(response?.status || 500, error, retrunMsg);
    } else {
      if (error?.message === "Failed to fetch") {
        showToast(
          "Cannot connect to server. Please check your network connection.",
          "error"
        );
      } else {
        showToast(error?.message, "error");
      }
      return null;
    }
  }
};
const handleResponseError = (status, errorMsg, retrunMsg = false) => {
  if (retrunMsg) {
    return { status: false, message: errorMsg, statusCode: status, data: null };
  } else if (status === 401 || status === 403) {
    showToast(errorMsg, "error");
    setTimeout(() => {
      // Clear localStorage
      localStorage.clear();
      // Redirect to login page
      window.location.href = "/";
    }, 300);
  } else if (status === 404) {
    showToast("Resource not found", "error");
  } else if (status === 500) {
    showToast("Internal server error", "error");
  } else {
    showToast(errorMsg, "error");
  }
};

export const handleResponseErrorData = (error, defaultReturn = null) => {
  if (error?.message === "Failed to fetch") {
    showToast(
      "Cannot connect to server. Please check your network connection.",
      "error"
    );
  } else {
    showToast(error?.message, "error");
  }
  return defaultReturn;
};

export const handleResponseErrorDataReturn = (error, defaultReturn = {}) => {
  return {
    success: false,
    message: error?.message || "Network error",
    statusCode: error?.status || 500,
    data: defaultReturn,
  };
};

export const isAuthenticated = () => {
  // Check if the user is authenticated based on your logic
  // For example, you might check if a token exists in local storage
  const token = localStorage.getItem("token");

  if (token) {
    try {
      // Decode the token to get the expiration time
      const decodedToken = JSON.parse(atob(token.split(".")[1]));
      const currentTime = Date.now() / 1000; // Convert current time to seconds

      if (decodedToken.exp < currentTime) {
        // Token is expired, remove it from local storage
        localStorage.removeItem("token");
        return false;
      }

      // Token is valid
      return true;
    } catch (error) {
      // Invalid token format or decoding error
      localStorage.removeItem("token");
      return false;
    }
  }

  // No token found
  return false;
};

export const apiCallWithAuth = async (
  url,
  method,
  body = null,
  headers = {},
  defaultReturn = null
) => {
  try {
    const token =
      headers?.token_type == "pat" ? await getPatToken() : await getToken();
    const response = await fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
        ...headers,
      },
      body,
    });

    return await processApiResponse(response);
  } catch (error) {
    return handleResponseErrorData(error, defaultReturn);
  }
};
export const apiCall = async (
  url,
  method,
  body = null,
  headers = {},
  defaultReturn = null
) => {
  try {
    const response = await fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        ...headers,
      },
      body,
    });

    return await processApiResponse(response, false);
  } catch (error) {
    return handleResponseErrorData(error, defaultReturn);
  }
};

export const apiCallWithAuthWithReturn = async (
  url,
  method,
  body = null,
  headers = {},
  defaultReturn = null,
  otherOption = {}
) => {
  try {
    const token =
      headers?.token_type == "pat" ? await getPatToken() : await getToken();
    const removedHeaders = {};
    const { removeContentType = false } = otherOption;
    if (!removeContentType) {
      removedHeaders["Content-Type"] = "application/json";
    }
    const response = await fetch(url, {
      method,
      headers: {
        ...removedHeaders,
        authorization: `Bearer ${token}`,
        ...headers,
      },
      body,
    });
    return await processApiResponse(response, true);
  } catch (error) {
    return handleResponseErrorData(error, defaultReturn);
  }
};

export const apiCallWithReturn = async (
  url,
  method,
  body = null,
  headers = {},
  defaultReturn = {}
) => {
  try {
    const response = await fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        ...headers,
      },
      body,
    });

    return await processApiResponse(response, true);
  } catch (error) {
    return handleResponseErrorDataReturn(error, null);
  }
};

export const getGlobalViewDateFormat = (forceDateFormatstring = "") => {
  return forceDateFormatstring === "" ? "YYYY-MM-DD" : forceDateFormatstring;
};

export const formatDateGlobal = (date, options = {}) => {
  const { currentFormat = null, newFormat = "D MMM, h:mm A", short = false } = options;
  const fmt = short ? "D MMM" : newFormat;
  if (currentFormat) {
    return moment(date, currentFormat).format(fmt);
  }
  return moment(date).format(fmt);
};

export const CustomInput = React.forwardRef(({ value, onClick }, ref) => (
  <div
    className={`custom-date-input min-w-[170px] min-h-[45px] px-5 outline-none border-2 border-gray-200 rounded-md focus:ring-blue-600 focus:border-blue-600  flex justify-between items-center cursor-pointer w-full`}
    onClick={onClick}
    ref={ref}
  >
    <span> {value}</span>
    <MdDateRange />
  </div>
));

export const getStartOrEndOfDay = (date, startOrEnd) => {
  if (startOrEnd === "start") {
    return moment(date).startOf("day").format("YYYY-MM-DD HH:mm:ss Z");
  } else if (startOrEnd === "end") {
    return moment(date).endOf("day").format("YYYY-MM-DD HH:mm:ss Z");
  } else if (startOrEnd === "startdayjs") {
    //console.log('date',date)
    return dayjs(date).startOf("day").format("YYYY-MM-DD HH:mm:ss Z");
  } else if (startOrEnd === "enddayjs") {
    return dayjs(date).endOf("day").format("YYYY-MM-DD HH:mm:ss Z");
  } else {
    throw new Error(
      'Invalid value for startOrEnd. Use either "start" or "end".'
    );
  }
};

export const localDate = (date) => {
  if (date instanceof Date) {
    return new Date(
      date.setMinutes(date.getMinutes() - date.getTimezoneOffset())
    );
  }
  return date;
};

export const downloadFile = async (url, fileName) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();

    // Use downloadjs to handle the download
    download(blob, fileName);
  } catch (error) {
    console.error("Error:", error);
    showToast("Error downloading file", "error");
  }
};

export const calculateAgeR = (dateOfBirth) => {
  const dob = new Date(parseDate(dateOfBirth));
  const now = new Date();

  // Calculate the difference in years
  let ageYears = now.getFullYear() - dob.getFullYear();
  const monthDiff = now.getMonth() - dob.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dob.getDate())) {
    ageYears--;
  }

  // Check if the age is within the newborn age range (up to 3 months)
  if (ageYears === 0 && monthDiff <= 3) {
    return "Newborn (1-3 months)";
  } else if (ageYears === 0 && monthDiff < 12) {
    return "Infant (<1 year)";
  } else {
    return ageYears;
  }
};

export const parseDate = (dateString) => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    throw new Error("Invalid date format");
  }
  const formattedDate = `${date.getFullYear()}-${(date.getMonth() + 1)
    .toString()
    .padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")}`;
  return formattedDate;
};

export const calculateDateOfBirthFromAgeR = (age) => {
  const currentYear = new Date().getFullYear();
  const birthYear = currentYear - age;
  // Create a new Date object with January 1st of the birth year
  const dateOfBirth = new Date(birthYear, 0, 1);
  return dateOfBirth;
};

export const getAgeStr = (age) => {
  let ageStr;
  if (isNumber(age)) {
    ageStr = `${age} ${age !== 1 ? "years" : "year"}`;
  } else {
    ageStr = age;
  }
  return ageStr;
};
